/**
 * 🛒 HEALING SOUNDS - EXTERNAL CHECKOUT SCRIPT
 * 
 * This script intercepts Shopify cart operations and redirects
 * checkout to our custom checkout page with Whop Payments.
 * 
 * Install: Add to theme.liquid before </body>
 * 
 * <script 
 *   src="{{ 'hs-checkout.js' | asset_url }}" 
 *   data-api-url="https://checkout.healing-sounds.com/api"
 *   data-checkout-url="https://checkout.healing-sounds.com"
 *   defer>
 * </script>
 */

(function initHealingSoundsCheckout() {
  // Prevent double initialization
  if (window.__HS_CHECKOUT_LOADED__) return;
  window.__HS_CHECKOUT_LOADED__ = true;

  console.log('%c🎵 [HS CHECKOUT] Initializing...', 'background: #8B5CF6; color: white; font-size: 14px; padding: 5px; font-weight: bold;');

  // ===========================================
  // CONFIGURATION
  // ===========================================
  
  const scriptTag = document.currentScript;
  const API_URL = scriptTag?.getAttribute('data-api-url') || 'https://hs-checkout-backend-production.up.railway.app/api';
  const CHECKOUT_URL = scriptTag?.getAttribute('data-checkout-url') || 'https://shop.healing-sounds.com';

  console.log('[HS CHECKOUT] Config:', { API_URL, CHECKOUT_URL });

  // UTM parameters + ad click ids to track (2026-10-01: same list as the PD checkout script).
  // gbraid/wbraid = Google iPhone/app click ids, msclkid = Microsoft/Bing, epik = Pinterest,
  // campaign_id/adgroup_id/ad_id/matchtype/network/device = Google Ads ValueTrack, tw_* = old templates,
  // oppref = OpenAI Ads click ref (Ecom/healing-sounds/docs/TRACKING.md §9).
  const TRACKING_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign',
    'utm_content', 'utm_term',
    'fbclid', 'gclid', 'ttclid',
    'gbraid', 'wbraid', 'msclkid',
    'tw_source', 'tw_campaign', 'tw_adid',
    'epik', 'utm_id', 'campaign_id', 'adgroup_id', 'ad_id',
    'matchtype', 'network', 'device', 'tw_kwdid',
    'oppref'
  ];

  // Platform cookies, read from document.cookie and sent with the click ids (server-side relay
  // match quality). Identity cookies (_fbp/_fbc/_ttp) fall back to the last seen value; the
  // click cookies (_gcl_aw/_uetmsclkid) are only ever read fresh (a stored copy could be stale).
  const COOKIE_TRACKING_KEYS = {
    _fbp: 'fbp',
    _fbc: 'fbc',
    _ttp: 'ttp',
    _gcl_aw: 'gcl_aw',
    _uetmsclkid: 'uetmsclkid'
  };
  const COOKIE_FRESH_ONLY = ['gcl_aw', 'uetmsclkid'];

  function getCookie(name) {
    const prefix = name + '=';
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const part of parts) {
      if (part.indexOf(prefix) === 0) {
        const raw = part.slice(prefix.length);
        let val;
        try { val = decodeURIComponent(raw); } catch { val = raw; }
        return val.slice(0, 512);
      }
    }
    return null;
  }

  // ===========================================
  // SESSION MANAGEMENT
  // ===========================================

  function getSessionId() {
    return localStorage.getItem('hs_session_id');
  }

  function setSessionId(sessionId) {
    localStorage.setItem('hs_session_id', sessionId);
    // Also set cookie for cross-domain
    try {
      document.cookie = `hs_session_id=${sessionId}; path=/; SameSite=Lax; max-age=86400`;
    } catch (e) {}
  }

  function clearSession() {
    localStorage.removeItem('hs_session_id');
    localStorage.removeItem('hs_cart');
  }

  // ===========================================
  // UTM TRACKING
  // ===========================================

  // 2026-10-01 — attribution « par clic » (même logique que le script PD, voir
  // Ecom/Primydeals/ads/TRACKING_ACCOUNTS.md §15). Avant : chaque clé était gardée sans fin, donc une
  // commande venue d'un email ou de Meta portait encore le gclid / les utm d'un ancien clic Google.
  //  - URL avec un clic pub NOUVEAU (gclid/gbraid/wbraid/msclkid/fbclid/ttclid/epik) = nouveau contact :
  //    clés de contexte (utm_*, campaign_id, tw_*…) et anciens ids de la MÊME plateforme effacés ;
  //  - ids des autres plateformes gardés ; contact courant noté dans hs_click_ref / hs_last_click ;
  //  - dernier clic Google + sa campagne gardés à part (hs_g_click_ref / hs_g_campaign_id) ;
  //  - URL sans clic mais autre source/campagne (email, partenaire) = contact sans clic ;
  //  - 30 jours sans contact = tout effacé.
  const CLICK_PLATFORMS = {
    google: ['gclid', 'gbraid', 'wbraid'],
    bing: ['msclkid'],
    meta: ['fbclid'],
    tiktok: ['ttclid'],
    pinterest: ['epik']
  };
  const CLICK_KEYS = Object.values(CLICK_PLATFORMS).reduce((all, keys) => all.concat(keys), []);
  const CONTEXT_KEYS = TRACKING_KEYS.filter(k => CLICK_KEYS.indexOf(k) === -1);
  const MARKER_KEYS = ['click_ref', 'last_click', 'touch_ts', 'g_click_ref', 'g_campaign_id'];
  const TOUCH_TTL_MS = 30 * 24 * 3600 * 1000;

  function lsGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
  function lsSet(key, val) { try { localStorage.setItem(key, val); } catch {} }
  function lsDel(key) { try { localStorage.removeItem(key); } catch {} }

  function persistTrackingParams() {
    let params;
    try { params = new URLSearchParams(window.location.search); } catch { return; }
    const now = Date.now();

    const ts = Number(lsGet('hs_touch_ts') || 0);
    if (ts && now - ts > TOUCH_TTL_MS) {
      TRACKING_KEYS.forEach(k => lsDel(`hs_${k}`));
      MARKER_KEYS.forEach(k => lsDel(`hs_${k}`));
      Object.values(COOKIE_TRACKING_KEYS).forEach(k => lsDel(`hs_${k}`));
    } else if (!ts && TRACKING_KEYS.some(k => lsGet(`hs_${k}`))) {
      lsSet('hs_touch_ts', String(now));
    }

    let urlClick = null;
    Object.keys(CLICK_PLATFORMS).some(platform => CLICK_PLATFORMS[platform].some(key => {
      const value = params.get(key);
      if (value) urlClick = { platform, key, value };
      return !!value;
    }));

    let newTouch = false;
    if (urlClick) {
      newTouch = lsGet('hs_click_ref') !== `${urlClick.key}:${urlClick.value}`.slice(0, 300);
    } else {
      const src = params.get('utm_source');
      const camp = params.get('utm_campaign');
      newTouch = !!((src && src !== lsGet('hs_utm_source')) || (camp && camp !== lsGet('hs_utm_campaign')));
    }

    if (newTouch) {
      CONTEXT_KEYS.forEach(k => lsDel(`hs_${k}`));
      if (urlClick) CLICK_PLATFORMS[urlClick.platform].forEach(k => lsDel(`hs_${k}`));
      lsSet('hs_click_ref', urlClick ? `${urlClick.key}:${urlClick.value}`.slice(0, 300) : 'none');
      lsSet('hs_last_click', urlClick ? urlClick.platform : 'none');
      lsSet('hs_touch_ts', String(now));
    }
    TRACKING_KEYS.forEach(key => {
      const val = params.get(key);
      if (val) lsSet(`hs_${key}`, val);
    });

    if (urlClick && urlClick.platform === 'google') {
      const gRef = `${urlClick.key}:${urlClick.value}`.slice(0, 300);
      const gCampaign = params.get('campaign_id') || params.get('tw_campaign');
      if (lsGet('hs_g_click_ref') !== gRef) {
        lsSet('hs_g_click_ref', gRef);
        if (gCampaign) lsSet('hs_g_campaign_id', gCampaign); else lsDel('hs_g_campaign_id');
      } else if (gCampaign) {
        lsSet('hs_g_campaign_id', gCampaign);
      }
    }
  }

  function getTrackingParams() {
    const params = {};
    TRACKING_KEYS.forEach(key => {
      const val = lsGet(`hs_${key}`);
      if (val) params[key] = val;
    });
    ['click_ref', 'last_click', 'g_click_ref', 'g_campaign_id'].forEach(key => {
      const val = lsGet(`hs_${key}`);
      if (val) params[key] = val;
    });
    Object.entries(COOKIE_TRACKING_KEYS).forEach(([cookieName, key]) => {
      const fresh = getCookie(cookieName);
      if (fresh) {
        params[key] = fresh;
        if (COOKIE_FRESH_ONLY.indexOf(key) === -1) lsSet(`hs_${key}`, fresh);
      } else if (COOKIE_FRESH_ONLY.indexOf(key) === -1) {
        const stored = lsGet(`hs_${key}`);
        if (stored) params[key] = stored;
      }
    });
    return params;
  }

  // Persist on page load
  persistTrackingParams();

  // ===========================================
  // CART SYNC
  // ===========================================

  let isSyncing = false;

  async function syncCart(cart) {
    if (isSyncing) {
      console.log('[HS CHECKOUT] ⏭️ Sync already in progress');
      return;
    }

    isSyncing = true;

    try {
      let items = [];
      let cartTotals = {};
      let shopifyCartToken = null;

      if (Array.isArray(cart)) {
        items = cart;
      } else if (cart && typeof cart === 'object') {
        items = cart.items || [];
        shopifyCartToken = cart.token?.split(/[?&]/)[0];
        cartTotals = {
          total_price: cart.total_price,
          original_total_price: cart.original_total_price,
          items_subtotal_price: cart.items_subtotal_price,
          total_discount: cart.total_discount || 0
        };
      }

      if (!items || items.length === 0) {
        console.log('[HS CHECKOUT] 🛑 Empty cart, skipping sync');
        return;
      }

      // 🎁 Free Gift pricing adjustment (2026-04-21)
      // Items flagged by free-gift-engine.js via properties._free_gift = "true"
      // → set their price-related fields to 0 so Whop checkout renders them as free.
      // Also adjust the cart totals to subtract the free gift value, so the user
      // never pays for the gift even if Shopify's cart still shows it at full price.
      let freeGiftValueCents = 0;
      items = items.map(item => {
        const isFreeGift = item.properties && item.properties._free_gift === 'true';
        if (!isFreeGift) return item;
        const lineValue = item.final_line_price || item.line_price || (item.price * item.quantity) || 0;
        freeGiftValueCents += lineValue;
        return {
          ...item,
          price: 0,
          final_price: 0,
          line_price: 0,
          original_line_price: 0,
          final_line_price: 0,
          total_discount: (item.total_discount || 0) + lineValue,
          _hs_free_gift: true,
          _hs_original_price: item.price
        };
      });

      if (freeGiftValueCents > 0) {
        cartTotals = {
          ...cartTotals,
          total_price: Math.max(0, (cartTotals.total_price || 0) - freeGiftValueCents),
          items_subtotal_price: Math.max(0, (cartTotals.items_subtotal_price || 0) - freeGiftValueCents),
          total_discount: (cartTotals.total_discount || 0) + freeGiftValueCents,
          _hs_free_gift_discount: freeGiftValueCents
        };
        console.log('[HS CHECKOUT] 🎁 Free gift discount applied:', freeGiftValueCents / 100, cart.currency || 'USD');
      }

      console.log('[HS CHECKOUT] 📦 Syncing cart:', items.length, 'items');

      // Detect country
      const country = window.Shopify?.country || 
                      localStorage.getItem('shopify_country') || 
                      null;

      const payload = {
        sessionId: getSessionId(),
        storeId: window.Shopify?.shop || 'healing-sounds',
        currentCart: items,
        currency: cart.currency || 'USD',
        shopify_cart_token: shopifyCartToken,
        country,
        order_attributes: getTrackingParams(),
        ...cartTotals
      };

      const res = await fetch(`${API_URL}/cart/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        console.warn('[HS CHECKOUT] ⚠️ Sync failed:', res.status);
        return;
      }

      const data = await res.json();

      if (data.session_id) {
        setSessionId(data.session_id);
        console.log('[HS CHECKOUT] ✅ Session:', data.session_id);
      }

      localStorage.setItem('hs_cart', JSON.stringify(data.session));

    } catch (err) {
      console.error('[HS CHECKOUT] ❌ Sync error:', err);
    } finally {
      isSyncing = false;
    }
  }

  // ===========================================
  // FETCH/XHR INTERCEPTION
  // ===========================================

  function isShopifyCartUrl(url) {
    try {
      const u = new URL(url, location.origin);
      if (u.origin !== location.origin) return false;
      const p = u.pathname;
      return (
        p === '/cart' ||
        p === '/cart.js' ||
        p.startsWith('/cart/add') ||
        p.startsWith('/cart/change') ||
        p.startsWith('/cart/update')
      );
    } catch {
      return false;
    }
  }

  function isCartMutationUrl(url) {
    try {
      const u = new URL(url, location.origin);
      if (u.origin !== location.origin) return false;
      const p = u.pathname;
      return p.startsWith('/cart/add') || p.startsWith('/cart/change') || p.startsWith('/cart/update');
    } catch {
      return false;
    }
  }

  function isJsonEndpoint(url) {
    return /\.js(\?|$)/.test(url);
  }

  // 🎁 2026-04-22 (Fix #E) : Shopify Section Rendering API injection.
  // Inject `sections: "cart-drawer"` into outgoing POST /cart/{add,change,update}.js bodies.
  // The response JSON will include `sections: { "cart-drawer": "<html>" }` — we extract
  // and dispatch `hs:drawerSectionReady` so slide-cart.js skips its own /cart?view=drawer refetch.
  // Saves one HTTP round-trip per cart mutation (~200-400ms perceived speedup).
  function injectSectionsParam(init) {
    try {
      if (!init || !init.body) return init;
      if (typeof init.body === 'string') {
        // Try JSON first (theme.js default)
        let parsed = null;
        try { parsed = JSON.parse(init.body); } catch {}
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          if (!parsed.sections) {
            parsed.sections = 'cart-drawer';
            init.body = JSON.stringify(parsed);
          }
          return init;
        }
        // Fallback: URL-encoded form body
        if (init.body.includes('=') && !init.body.includes('sections=')) {
          init.body = init.body + '&sections=cart-drawer';
        }
      } else if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
        if (!init.body.has('sections')) init.body.append('sections', 'cart-drawer');
      } else if (typeof URLSearchParams !== 'undefined' && init.body instanceof URLSearchParams) {
        if (!init.body.has('sections')) init.body.append('sections', 'cart-drawer');
      }
    } catch (e) { /* skip — don't break request */ }
    return init;
  }

  async function extractAndDispatchSection(res) {
    try {
      if (!res.ok) return;
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('json')) return;
      const json = await res.json();
      const html = json.sections && json.sections['cart-drawer'];
      if (!html) return;
      window.__HS_LATEST_DRAWER_HTML__ = html;
      window.__HS_LATEST_DRAWER_AT__ = Date.now();
      document.dispatchEvent(new CustomEvent('hs:drawerSectionReady', {
        detail: { html, at: window.__HS_LATEST_DRAWER_AT__ }
      }));
    } catch (e) { /* silent */ }
  }

  // Patch fetch
  const origFetch = window.fetch;
  window.fetch = async function(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = ((init && init.method) || 'GET').toUpperCase();

    // Fix #E : inject sections param on outgoing cart mutation POSTs
    if (method === 'POST' && isCartMutationUrl(url)) {
      init = injectSectionsParam(init);
      // Sync arguments[1] so origFetch.apply sees the modified init
      if (arguments.length >= 2) arguments[1] = init;
    }

    const res = await origFetch.apply(this, arguments);

    // Fix #E : extract section HTML from response, dispatch event
    if (method === 'POST' && isCartMutationUrl(url)) {
      extractAndDispatchSection(res.clone()).catch(() => {});
    }

    if (isShopifyCartUrl(url)) {
      // 🚀 2026-04-22 : non-blocking Whop sync (fire-and-forget).
      //
      // PREVIOUSLY: awaited Whop sync BEFORE returning response → every cart mutation blocked
      // for ~500-1000ms (100ms sleep + /cart.js + syncCart to Whop backend).
      // Compounded with Fix #E: each engine gift remove blocked too → 2 × blocking = 2s
      // delay user reported when removing gong.
      //
      // NOW: run in background. Response returns immediately. Whop state is eventually
      // consistent; checkout page does its own sync before redirect, so no race risk.
      (async () => {
        try {
          await new Promise(r => setTimeout(r, 100));
          const cartRes = await origFetch('/cart.js');
          if (cartRes.ok) {
            const fullCart = await cartRes.json();
            await syncCart(fullCart);
          }
        } catch (err) {
          console.warn('[HS CHECKOUT] Cart fetch error:', err);
        }
      })();
    }

    return res;
  };

  // Patch XHR
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._hsUrl = url;
    this._hsIsCart = isShopifyCartUrl(url);
    this._hsIsJson = isJsonEndpoint(url);
    return origOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function() {
    if (this._hsIsCart) {
      this.addEventListener('load', async () => {
        if (this._hsIsJson && this.responseText) {
          try {
            await new Promise(r => setTimeout(r, 100));
            const cartRes = await fetch('/cart.js');
            if (cartRes.ok) {
              const fullCart = await cartRes.json();
              await syncCart(fullCart);
            }
          } catch (err) {
            console.warn('[HS CHECKOUT] XHR cart error:', err);
          }
        }
      });
    }
    return origSend.apply(this, arguments);
  };

  // ===========================================
  // CHECKOUT BUTTON INTERCEPTION
  // ===========================================

  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'button#checkout',
    '#CartDrawer-Checkout',
    '.cart__checkout-button',
    '.cart__checkout',
    '.checkout-button',
    'a[href$="/checkout"]',
    'a[href^="/checkout"]',
    'a[href*="/checkout?"]',
    'form[action="/cart"] [name="checkout"]',
    '[data-hs-checkout]'
  ];

  function isCheckoutElement(target) {
    if (!target) return false;
    
    // Skip if marked to ignore
    if (target.hasAttribute('data-hs-skip')) return false;
    if (target.closest('[data-hs-skip]')) return false;

    // Check against selectors
    for (const sel of CHECKOUT_SELECTORS) {
      if (target.matches?.(sel) || target.closest?.(sel)) {
        // Skip accelerated checkout (Apple Pay, etc.)
        if (target.closest('.shopify-payment-button')) return false;
        return true;
      }
    }
    return false;
  }

  async function proceedToCheckout() {
    console.log('%c🛒 [HS CHECKOUT] Proceeding to checkout...', 'background: #EC4899; color: white; font-size: 16px; padding: 8px; font-weight: bold;');

    try {
      // Sync cart one more time
      const cartRes = await fetch('/cart.js');
      if (cartRes.ok) {
        const fullCart = await cartRes.json();
        await syncCart(fullCart);
      }

      // Build checkout URL
      const sessionId = getSessionId();
      const params = new URLSearchParams(getTrackingParams());
      
      if (sessionId) {
        params.set('sid', sessionId);
      }

      const checkoutUrl = `${CHECKOUT_URL}/checkout?${params.toString()}`;
      console.log('[HS CHECKOUT] Redirecting to:', checkoutUrl);

      window.location.href = checkoutUrl;

    } catch (err) {
      console.error('[HS CHECKOUT] ❌ Checkout error:', err);
      // Fallback to native Shopify checkout
      window.location.href = '/checkout';
    }
  }

  // Expose globally for manual triggers
  window.hsCheckout = proceedToCheckout;

  // ===========================================
  // EVENT LISTENERS
  // ===========================================

  // Click handler (capture phase)
  document.addEventListener('click', async (e) => {
    if (isCheckoutElement(e.target)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      await proceedToCheckout();
    }
  }, true);

  // Form submit handler
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = (form.getAttribute('action') || '').toLowerCase();
    const hasCheckoutInput = !!form.querySelector('[name="checkout"]');

    if (action.includes('/cart') && hasCheckoutInput) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      await proceedToCheckout();
    }
  }, true);

  // ===========================================
  // BUTTON PATCHING (for dynamic content)
  // ===========================================

  function patchCheckoutButtons(root = document) {
    root.querySelectorAll(CHECKOUT_SELECTORS.join(', ')).forEach(btn => {
      if (btn.dataset.hsPatched) return;
      
      btn.type = 'button';
      btn.removeAttribute('form');
      btn.dataset.hsPatched = 'true';
    });
  }

  // Initial patch
  patchCheckoutButtons();

  // Observe DOM changes
  const observer = new MutationObserver(() => patchCheckoutButtons());
  observer.observe(document.body, { childList: true, subtree: true });

  // Handle back/forward cache
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      console.log('[HS CHECKOUT] ♻️ Restored from cache');
      patchCheckoutButtons();
    }
  });

  // ===========================================
  // INITIALIZATION
  // ===========================================

  document.addEventListener('DOMContentLoaded', async () => {
    console.log('[HS CHECKOUT] DOM ready');

    // Check if returning from completed checkout
    const sessionId = getSessionId();
    if (sessionId) {
      try {
        const res = await fetch(`${API_URL}/cart/check-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'completed') {
            clearSession();
            await fetch('/cart/clear.js', { method: 'POST' });
            console.log('[HS CHECKOUT] ✅ Order completed, cart cleared');
          }
        }
      } catch (err) {
        console.warn('[HS CHECKOUT] Status check error:', err);
      }
    }

    // Initial cart sync
    try {
      const cartRes = await fetch('/cart.js');
      if (cartRes.ok) {
        const cart = await cartRes.json();
        if (cart.items?.length > 0) {
          await syncCart(cart);
        }
      }
    } catch (err) {
      console.warn('[HS CHECKOUT] Initial sync error:', err);
    }
  });

  console.log('%c✅ [HS CHECKOUT] Ready!', 'background: #10B981; color: white; font-size: 14px; padding: 5px; font-weight: bold;');

})();
